import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePreservedCallback } from '@toss/react';
import {
  loadTossPayments,
  TossPaymentsWidgets,
} from '@tosspayments/tosspayments-sdk';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';

import {
  useCancelPurchaseOnetimePlan,
  useGetOnetimeOrderInfo,
  usePurchaseOnetimePlan,
  ZepApiError,
} from '@zep/apis';
import { useAuth } from '@zep/auth';
import { useDialog } from '@zep/dialog';

import { stashGtmPurchaseAmount } from '../../../../utils/gtmMarketingEvent';
import { PAYMENT_METHOD_SELECTOR } from '../modals';

// 국내 플랜 결제에 일시납에서만 사용됩니다.
export const usePlanOnetimeAction = ({
  price,
  spaceHashId,
  spacePlanTemplateId,
  onetimeStartDate,
  spacePlanHashId: initialSpacePlanHashId,
}: {
  price: number;
  spaceHashId: string;
  spacePlanTemplateId: number;
  onetimeStartDate?: Date;
  spacePlanHashId?: string;
}) => {
  const { t } = useTranslation(['subscription']);
  const { user } = useAuth();
  const [paymentState, setPaymentState] = useState<{
    widgets: TossPaymentsWidgets | null;
    isWidgetReady: boolean;
  }>({
    widgets: null,
    isWidgetReady: false,
  });

  const dialog = useDialog();
  const { mutateAsync: purchaseOnetimePlan } = usePurchaseOnetimePlan();
  const { mutateAsync: cancelPurchaseOnetimePlan } =
    useCancelPurchaseOnetimePlan();
  const { mutateAsync: getOrderInfo } = useGetOnetimeOrderInfo();

  // Dialogs
  const showAlert = usePreservedCallback(async (message: string) => {
    return await dialog.alert({
      title: t('subscription:payment.alertFailPay.title'),
      content: message,
    });
  });

  const showRetryConfirm = usePreservedCallback(async (error: ZepApiError) => {
    return await dialog.confirm({
      title: t('subscription:payment.alertFailPay.title'),
      content: t('subscription:payment.alertFailPay.descriptionErrorCode', {
        errorCode: error?.statusKey,
      }),
      confirmText: '재시도',
    });
  });

  // Memoize customer key based on user
  const customerKey = useMemo(() => user?.hashId || 'ANONYMOUS', [user]);

  const loadPaymentWidget = useCallback(async () => {
    const tossPayments = await loadTossPayments(
      process.env.NEXT_PUBLIC_TOSS_PAYMENT_WIDGET_CLIENT_KEY || '',
    );

    const widgets = tossPayments.widgets({
      customerKey,
    });

    setPaymentState({ widgets, isWidgetReady: false });
  }, [customerKey]);

  // Render payment methods on the widget
  const renderWidgets = useCallback(async () => {
    const { widgets } = paymentState;
    if (!widgets) return;

    await widgets.setAmount({ value: price, currency: 'KRW' });
    await widgets.renderPaymentMethods({
      selector: `#${PAYMENT_METHOD_SELECTOR}`,
      variantKey: price < 3_000_000 ? 'ONETIME_UNDER_300' : 'ONETIME_OVER_300',
    });

    setPaymentState(prevState => ({ ...prevState, isWidgetReady: true }));
  }, [price, paymentState]);

  // Handle the payment process
  const handlePayment = useCallback(async () => {
    const { widgets } = paymentState;
    if (!widgets) return;

    let spacePlanHashId = initialSpacePlanHashId;
    let orderInfoStatus: number | null = null;
    let orderInfo: {
      orderId: string;
      priceInfo?: { finalPrice: number };
    } | null = null;

    try {
      // Initiate the purchase request
      const purchaseRes = await purchaseOnetimePlan({
        spaceHashId,
        spacePlanTemplateId,
        startDate: format(onetimeStartDate as Date, 'yyyy-MM-dd'),
        spacePlanHashId,
      });

      if (
        purchaseRes.status === 0 &&
        purchaseRes.data.spacePlan?.spacePlanHashId
      ) {
        spacePlanHashId = purchaseRes.data.spacePlan.spacePlanHashId;
        const orderInfoRes = await getOrderInfo({ spacePlanHashId });
        orderInfoStatus = orderInfoRes.status;
        orderInfo = orderInfoRes.data;
      }

      // Proceed to TossPayment if order info is valid
      if (orderInfoStatus === 0 && !!orderInfo?.orderId) {
        // 서버 VAT snapshot(finalPrice)이 내려오면 Toss amount를 서버 값으로 동기화한다.
        // 클라 preview 금액은 서버 응답을 받기 전의 표시용일 뿐, PG amount의
        // source of truth는 서버 snapshot이다 (Gate D: Toss amount 일치).
        const serverFinalPrice = orderInfo.priceInfo?.finalPrice;
        if (
          typeof serverFinalPrice === 'number' &&
          serverFinalPrice !== price
        ) {
          await widgets.setAmount({ value: serverFinalPrice, currency: 'KRW' });
        }
        // 완료 페이지의 GTM purchase 신호용으로, PG에 실제 전달되는 청구액
        // (서버 VAT snapshot 우선)을 보관한다. Toss 리다이렉트로 인메모리
        // 값이 유실되기 때문이다. 일시납은 KRW 전용.
        stashGtmPurchaseAmount(orderInfo.orderId, {
          value:
            typeof serverFinalPrice === 'number' ? serverFinalPrice : price,
          currency: 'KRW',
        });
        await widgets.requestPayment({
          orderId: orderInfo.orderId,
          orderName: orderInfo.orderId,
          successUrl: `${window.location.origin}/api/space-plans/product-onetime/toss/callback/success`,
          failUrl: `${window.location.origin}/plan-payment?spaceHashId=${spaceHashId}`,
          customerEmail: user?.snsId || '',
          customerName: user?.name,
        });
      }
    } catch (error: any) {
      console.error('Error during payment:', error);

      // Cancel the plan if payment fails
      if (spacePlanHashId) {
        await cancelPurchaseOnetimePlan({ spaceHashId, spacePlanHashId });
      }

      // Error handling: Retry or show alert
      if (error instanceof ZepApiError) {
        const retry = await showRetryConfirm(error);
        if (retry) await handlePayment();
      } else {
        await showAlert(error.message);
      }
    }
  }, [
    paymentState,
    purchaseOnetimePlan,
    getOrderInfo,
    cancelPurchaseOnetimePlan,
    initialSpacePlanHashId,
    spaceHashId,
    spacePlanTemplateId,
    onetimeStartDate,
    user,
    showAlert,
    showRetryConfirm,
    price,
  ]);

  // Render widgets when they're loaded but not ready
  useEffect(() => {
    if (paymentState.widgets && !paymentState.isWidgetReady) {
      renderWidgets();
    }
  }, [paymentState, renderWidgets]);

  useEffect(() => {
    loadPaymentWidget();
  }, [loadPaymentWidget]);

  return {
    isWidgetReady: paymentState.isWidgetReady,
    handleClickOnetimePayment: handlePayment,
  };
};
